import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import ProfileAvatarImage from '@/components/ProfileAvatarImage';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar, Shield, Sparkles, Edit2, Camera, Loader2, Tag, Plus, X, Ban, VolumeX, ShieldOff, Eraser, Eye, ImagePlus, Lock, Users, UserPlus, Clock, Send, ShieldAlert, Mail, Crown, Flag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { TierBadge, getTierRingClass, getTierCoverGradient } from '@/components/TierBadge';
import { API_URL } from '@/lib/api';
const DEFAULT_AVATAR = '/shh-logo-new.jpeg';

// Calculate profile completion from fields
const calcCompletion = (p) => {
  if (!p) return { pct: 0, filled: 0, total: 5 };
  const fields = [
    !!p.display_name,
    !!p.bio,
    !!p.profile_headline,
    !!p.profile_picture,
    !!p.profile_image_url_private];
  const filled = fields.filter(Boolean).length;
  return { pct: Math.round((filled / 5) * 100), filled, total: 5 };
};

const getPhotoFrame = (profile, type) => {
  if (type === 'private') {
    return {
      zoom: Number(profile?.profile_image_private_zoom ?? 1),
      positionX: Number(profile?.profile_image_private_position_x ?? 50),
      positionY: Number(profile?.profile_image_private_position_y ?? 50),
    };
  }
  return {
    zoom: Number(profile?.profile_picture_zoom ?? 1),
    positionX: Number(profile?.profile_picture_position_x ?? 50),
    positionY: Number(profile?.profile_picture_position_y ?? 50),
  };
};

const clampFrameValue = (value, min, max) => Math.min(max, Math.max(min, value));

const getTouchDistance = (touches) => {
  if (!touches || touches.length < 2) return 0;
  const [a, b] = touches;
  const dx = a.clientX - b.clientX;
  const dy = a.clientY - b.clientY;
  return Math.hypot(dx, dy);
};

export default function ProfilePage() {
  const { userId } = useParams();
  const { user: currentUser, updateUser, canReport } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSetupMode = searchParams.get('setup') === '1';

  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadType, setUploadType] = useState('public'); // 'public' | 'private'
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showPhotoViewDialog, setShowPhotoViewDialog] = useState(false);
  const [viewingPhotoUrl, setViewingPhotoUrl] = useState(null);
  const [showPhotoOptionsDialog, setShowPhotoOptionsDialog] = useState(false);
  const [photoOptionsType, setPhotoOptionsType] = useState('public');
  const [imageFrame, setImageFrame] = useState({ zoom: 1, positionX: 50, positionY: 50 });
  const [newTag, setNewTag] = useState('');
  const [addingTag, setAddingTag] = useState(false);
  const [blockStatus, setBlockStatus] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [modInfo, setModInfo] = useState(null);
  const [allBadges, setAllBadges] = useState([]);
  const [showMuteDialog, setShowMuteDialog] = useState(false);
  const [muteDays, setMuteDays] = useState('1');
  const [muteReason, setMuteReason] = useState('');
  const [showBanDialog, setShowBanDialog] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [updatingBanner, setUpdatingBanner] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editRealName, setEditRealName] = useState('');
  const [editBio, setEditBio] = useState('');
  const [editHeadline, setEditHeadline] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [confidantStatus, setConfidantStatus] = useState(null); // 'none', 'pending_sent', 'pending_received', 'confidants', 'cooldown'
  const [showConfidantDialog, setShowConfidantDialog] = useState(false);
  const [confidantMessage, setConfidantMessage] = useState('');
  const [sendingRequest, setSendingRequest] = useState(false);
  const [showReportNameDialog, setShowReportNameDialog] = useState(false);
  const [reportNameReason, setReportNameReason] = useState('');
  const [reportingName, setReportingName] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const fileInputRef = useRef(null);
  const adminBannerInputRef = useRef(null);
  const previewGestureRef = useRef(null);
  const dragStateRef = useRef(null);
  const pinchStateRef = useRef(null);

  const isOwnProfile = currentUser?.user_id === userId;
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';
  const isUnverified = currentUser && !currentUser.email_verified && !currentUser.oauth_provider;

  // Resend verification cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => setResendCooldown(c => Math.max(0, c - 1)), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification`);
      toast.success('Verification email sent! Reply to it to verify your account.');
      setResendCooldown(300); // 5 minutes
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to send verification email';
      if (error.response?.status === 429) {
        setResendCooldown(300);
      }
      toast.error(detail);
    } finally {
      setResendingVerification(false);
    }
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        
        const [profileRes, statsRes] = await Promise.all([
          axios.get(`${API_URL}/api/users/${userId}/profile`),
          axios.get(`${API_URL}/api/users/${userId}/stats`)
        ]);
        setProfile(profileRes.data);
        setStats(statsRes.data);

        if (currentUser && !isOwnProfile) {
          try {
            const [blockRes, confStatusRes] = await Promise.all([
              axios.get(`${API_URL}/api/users/${userId}/block-status`),
              axios.get(`${API_URL}/api/users/${userId}/confidant-status`)
            ]);
            setBlockStatus(blockRes.data.is_blocked);
            setConfidantStatus(confStatusRes.data.status);
          } catch (e) {}
        }

        if (isAdmin) {
          try {
            const [modRes, badgesRes] = await Promise.all([
              axios.get(`${API_URL}/api/admin/users/${userId}/moderation`),
              axios.get(`${API_URL}/api/admin/badges`)
            ]);
            setModInfo(modRes.data);
            setAllBadges(badgesRes.data.badges || []);
          } catch (e) {}
        } else {
          try {
            const badgesRes = await axios.get(`${API_URL}/api/badges`);
            setAllBadges(badgesRes.data.badges || []);
          } catch (e) {}
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [userId, currentUser, isAdmin, isOwnProfile]);

  const getProfileImageUrl = (pic) => {
    if (!pic) return DEFAULT_AVATAR;
    if (pic.startsWith('/api/')) return `${API_URL}${pic}`;
    return pic;
  };

  // Photo click handlers
  const handlePublicPhotoClick = () => {
    if (isOwnProfile) {
      setPhotoOptionsType('public');
      setShowPhotoOptionsDialog(true);
    } else {
      const url = getProfileImageUrl(profile?.profile_picture);
      if (url !== DEFAULT_AVATAR) {
        setViewingPhotoUrl(url);
        setShowPhotoViewDialog(true);
      }
    }
  };

  const handlePrivatePhotoClick = () => {
    if (isOwnProfile) {
      setPhotoOptionsType('private');
      setShowPhotoOptionsDialog(true);
    } else {
      const url = getProfileImageUrl(profile?.profile_image_url_private);
      if (url !== DEFAULT_AVATAR) {
        setViewingPhotoUrl(url);
        setShowPhotoViewDialog(true);
      }
    }
  };

  const openUploadDialog = (type) => {
    setUploadType(type);
    setPreviewUrl(null);
    setImageFrame(getPhotoFrame(profile, type));
    setShowPhotoOptionsDialog(false);
    setShowUploadDialog(true);
  };

  const viewCurrentPhoto = (type) => {
    const pic = type === 'public' ? profile?.profile_picture : profile?.profile_image_url_private;
    const url = getProfileImageUrl(pic);
    setShowPhotoOptionsDialog(false);
    if (url !== DEFAULT_AVATAR) {
      setViewingPhotoUrl(url);
      setShowPhotoViewDialog(true);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    setPreviewUrl(URL.createObjectURL(file));
  };

  const updateFrameFromDelta = (deltaX, deltaY) => {
    const container = previewGestureRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    setImageFrame((frame) => ({
      ...frame,
      positionX: clampFrameValue(frame.positionX + (deltaX / rect.width) * 100, 0, 100),
      positionY: clampFrameValue(frame.positionY + (deltaY / rect.height) * 100, 0, 100),
    }));
  };

  const handlePreviewPointerDown = (event) => {
    if (event.pointerType === 'touch') return;
    event.preventDefault();
    dragStateRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePreviewPointerMove = (event) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;
    event.preventDefault();
    const deltaX = event.clientX - dragState.x;
    const deltaY = event.clientY - dragState.y;
    dragStateRef.current = { ...dragState, x: event.clientX, y: event.clientY };
    updateFrameFromDelta(deltaX, deltaY);
  };

  const handlePreviewPointerUp = (event) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;
    dragStateRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const handlePreviewWheel = (event) => {
    event.preventDefault();
    setImageFrame((frame) => ({
      ...frame,
      zoom: clampFrameValue(frame.zoom - event.deltaY * 0.0025, 1, 3),
    }));
  };

  const handlePreviewTouchStart = (event) => {
    if (event.touches.length === 2) {
      pinchStateRef.current = {
        distance: getTouchDistance(event.touches),
      };
      dragStateRef.current = null;
      return;
    }

    if (event.touches.length === 1) {
      const touch = event.touches[0];
      dragStateRef.current = { x: touch.clientX, y: touch.clientY };
      pinchStateRef.current = null;
    }
  };

  const handlePreviewTouchMove = (event) => {
    if (event.touches.length === 2 && pinchStateRef.current) {
      event.preventDefault();
      const distance = getTouchDistance(event.touches);
      const previousDistance = pinchStateRef.current.distance || distance;
      const delta = (distance - previousDistance) / 160;
      pinchStateRef.current = { distance };
      setImageFrame((frame) => ({
        ...frame,
        zoom: clampFrameValue(frame.zoom + delta, 1, 3),
      }));
      return;
    }

    if (event.touches.length === 1 && dragStateRef.current) {
      event.preventDefault();
      const touch = event.touches[0];
      const deltaX = touch.clientX - dragStateRef.current.x;
      const deltaY = touch.clientY - dragStateRef.current.y;
      dragStateRef.current = { x: touch.clientX, y: touch.clientY };
      updateFrameFromDelta(deltaX, deltaY);
    }
  };

  const handlePreviewTouchEnd = (event) => {
    if (event.touches.length === 0) {
      dragStateRef.current = null;
      pinchStateRef.current = null;
    } else if (event.touches.length === 1) {
      const touch = event.touches[0];
      dragStateRef.current = { x: touch.clientX, y: touch.clientY };
      pinchStateRef.current = null;
    }
  };

  const handleUpload = async () => {
    const file = fileInputRef.current?.files[0];
    const hasExistingPhoto = uploadType === 'private' ? !!profile?.profile_image_url_private : !!profile?.profile_picture;
    if (!file && !hasExistingPhoto) return;

    setUploading(true);
    try {
      let response;
      if (file) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('zoom', String(imageFrame.zoom));
        formData.append('position_x', String(imageFrame.positionX));
        formData.append('position_y', String(imageFrame.positionY));

        response = await axios.post(
          `${API_URL}/api/users/me/profile-image?image_type=${uploadType}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
          }
        );
      } else {
        response = await axios.put(
          `${API_URL}/api/users/me/profile-image-settings?image_type=${uploadType}&zoom=${imageFrame.zoom}&position_x=${imageFrame.positionX}&position_y=${imageFrame.positionY}`
        );
      }

      const patch = response.data.updated_fields || {};
      setProfile(p => ({ ...p, ...patch }));
      updateUser(patch);
      setShowUploadDialog(false);
      setPreviewUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      toast.success(uploadType === 'private' ? 'Private photo updated!' : 'Profile photo updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleAddTag = async () => {
    if (!newTag.trim()) return;
    setAddingTag(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${userId}/tags`,
        { tag_name: newTag.trim() },
        
      );
      toast.success(`Tag "${newTag}" added`);
      setNewTag('');
      const response = await axios.get(`${API_URL}/api/users/${userId}/profile`);
      setProfile(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add tag');
    } finally {
      setAddingTag(false);
    }
  };

  const handleRemoveTag = async (tagName) => {
    try {
      await axios.delete(
        `${API_URL}/api/admin/users/${userId}/tags/${encodeURIComponent(tagName)}`,
        
      );
      toast.success(`Tag "${tagName}" removed`);
      const response = await axios.get(`${API_URL}/api/users/${userId}/profile`);
      setProfile(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove tag');
    }
  };

  const handleBlockUser = async () => {
    setBlocking(true);
    try {
      await axios.post(
        `${API_URL}/api/users/${userId}/block`,
        
      );
      setBlockStatus(true);
      setShowBlockConfirm(false);
      toast.success('User blocked. Their comments will be hidden from you.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block user');
    } finally {
      setBlocking(false);
    }
  };

  const handleMuteUser = async () => {
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${userId}/mute`,
        { days: parseInt(muteDays), reason: muteReason || null },
        
      );
      toast.success(`User muted for ${muteDays} day(s)`);
      setShowMuteDialog(false);
      setMuteReason('');
      const modRes = await axios.get(`${API_URL}/api/admin/users/${userId}/moderation`);
      setModInfo(modRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mute user');
    }
  };

  const handleUnmuteUser = async () => {
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${userId}/unmute`,
        
      );
      toast.success('User unmuted');
      const modRes = await axios.get(`${API_URL}/api/admin/users/${userId}/moderation`);
      setModInfo(modRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unmute user');
    }
  };

  const handleBanUser = async () => {
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${userId}/ban`,
        { reason: banReason || null },
        
      );
      toast.success('User banned permanently');
      setShowBanDialog(false);
      setBanReason('');
      const modRes = await axios.get(`${API_URL}/api/admin/users/${userId}/moderation`);
      setModInfo(modRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to ban user');
    }
  };

  const handleUnbanUser = async () => {
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${userId}/unban`,
        
      );
      toast.success('User unbanned');
      const modRes = await axios.get(`${API_URL}/api/admin/users/${userId}/moderation`);
      setModInfo(modRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unban user');
    }
  };

  const handleClearBlocks = async () => {
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/users/${userId}/clear-blocks`,
        
      );
      toast.success(res.data.message);
      const modRes = await axios.get(`${API_URL}/api/admin/users/${userId}/moderation`);
      setModInfo(modRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear blocks');
    }
  };

  const openEditProfile = () => {
    setEditDisplayName(profile?.display_name || profile?.username || '');
    setEditRealName(profile?.real_name || '');
    setEditBio(profile?.bio || '');
    setEditHeadline(profile?.profile_headline || '');
    setShowEditProfile(true);
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const update = {};
      if (editDisplayName.trim()) update.display_name = editDisplayName.trim();
      if (editRealName !== (profile?.real_name || '')) update.real_name = editRealName.trim();
      if (editBio !== (profile?.bio || '')) update.bio = editBio.trim();
      if (editHeadline !== (profile?.profile_headline || '')) update.profile_headline = editHeadline.trim();

      const updateRes = await axios.put(
        `${API_URL}/api/users/me`,
        update,
        
      );
      toast.success('Profile updated!');
      setShowEditProfile(false);
      updateUser(updateRes.data);
      const response = await axios.get(`${API_URL}/api/users/${userId}/profile`);
      setProfile(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSendConfidantRequest = async () => {
    setSendingRequest(true);
    try {
      await axios.post(
        `${API_URL}/api/users/${userId}/confidant-request`,
        { message: confidantMessage.trim() || null },
        
      );
      toast.success('Confidant request sent!');
      setShowConfidantDialog(false);
      setConfidantMessage('');
      setConfidantStatus('pending_sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setSendingRequest(false);
    }
  };

  const handleAdminBannerSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    setUpdatingBanner(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/users/${userId}/banner`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProfile((prev) => ({ ...prev, banner_image: res.data.banner_image }));
      toast.success('Cover picture updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update cover picture');
    } finally {
      setUpdatingBanner(false);
    }
  };

  const handleReportDisplayName = async () => {
    setReportingName(true);
    try {
      await axios.post(
        `${API_URL}/api/users/${userId}/report-display-name`,
        { reason: reportNameReason || null },
      );
      toast.success('Username reported. An admin will review it.');
      setShowReportNameDialog(false);
      setReportNameReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to report username');
    } finally {
      setReportingName(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex flex-col items-center">
          <Skeleton className="w-32 h-32 rounded-full mb-4" />
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <h1 className="font-serif text-2xl font-semibold mb-2">User Not Found</h1>
        <p className="text-muted-foreground">This user doesn't exist or has been removed.</p>
      </div>
    );
  }

  const joinDate = profile.created_at
    ? format(new Date(profile.created_at), 'MMMM yyyy')
    : 'Unknown';

  const completion = isOwnProfile ? calcCompletion(profile) : null;

  const getRoleBadge = (role) => {
    switch (role) {
      case 'superadmin':
        return <Badge className="bg-gold text-white"><Shield className="w-3 h-3 mr-1" /> Super Admin</Badge>;
      case 'admin':
        return <Badge className="bg-forest-700 text-white"><Shield className="w-3 h-3 mr-1" /> Admin</Badge>;
      default:
        return null;
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="profile-page">
      {/* Profile Setup Banner */}
      {(isSetupMode || (isOwnProfile && currentUser?.must_change_display_name)) && isOwnProfile && (
        <Card className="mb-6 border-amber-500/50 bg-amber-50/50 dark:bg-amber-900/10" data-testid="profile-setup-banner">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Edit2 className="w-5 h-5 text-amber-600 dark:text-amber-500 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-800 dark:text-amber-400">
                  {currentUser?.must_change_display_name ? 'Choose a New Username' : 'Complete Your Profile'}
                </h3>
                <p className="text-sm text-amber-700 dark:text-amber-500/80">
                  {currentUser?.must_change_display_name
                    ? 'Your previous username was flagged by moderation. Pick a new one before continuing in the community.'
                    : 'Set your display name to start participating in the community. Click "Edit Profile" below to get started.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email Verification Banner */}
      {isOwnProfile && isUnverified && (
        <Card className="mb-6 border-amber-500/50 bg-amber-50/50 dark:bg-amber-900/10" data-testid="verify-email-banner">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 text-amber-600 dark:text-amber-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800 dark:text-amber-400">Verify Your Email</h3>
                <p className="text-sm text-amber-700 dark:text-amber-500/80 mt-1">
                  Check your inbox for a verification email from Secrets Club. Simply <strong>reply to that email</strong> to verify your account and unlock full access to commenting, reactions, and more.
                </p>
                <div className="mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResendVerification}
                    disabled={resendingVerification || resendCooldown > 0}
                    className="border-amber-500/50 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                    data-testid="resend-verification-btn"
                  >
                    {resendingVerification ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Mail className="w-4 h-4 mr-2" />
                    )}
                    {resendCooldown > 0
                      ? `Resend in ${Math.floor(resendCooldown / 60)}:${String(resendCooldown % 60).padStart(2, '0')}`
                      : 'Resend verification email'}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Card className="overflow-hidden">
          {/* Cover/Header — tier-specific gradient for premium members, or custom banner */}
          {profile.banner_image ? (
            <div className="h-32 md:h-48 bg-cover bg-center" style={{ backgroundImage: `url(${API_URL}${profile.banner_image})` }} data-testid="profile-cover-banner" />
          ) : (
            <div className={`h-32 md:h-48 bg-gradient-to-br ${getTierCoverGradient(profile.premium_tier) || 'from-forest-800 to-forest-950 dark:from-forest-600 dark:to-forest-900'}`} data-testid="profile-cover-banner" />
          )}

          <CardContent className="relative pt-0">
            {/* Avatar Section - Public Photo */}
            <div className="flex flex-col items-center -mt-16 md:-mt-20">
              <div
                className={`relative ${isOwnProfile || (profile.profile_picture && profile.profile_picture !== DEFAULT_AVATAR) ? 'cursor-pointer group' : ''}`}
                onClick={handlePublicPhotoClick}
                data-testid="profile-avatar-container"
              >
                <Avatar className={`w-32 h-32 md:w-40 md:h-40 border-4 border-background shadow-lg ${getTierRingClass(profile.premium_tier, 'lg')}`}>
                  <ProfileAvatarImage
                    src={getProfileImageUrl(profile.profile_picture)}
                    alt={profile.display_name || profile.username}
                    zoom={profile.profile_picture_zoom}
                    positionX={profile.profile_picture_position_x}
                    positionY={profile.profile_picture_position_y}
                  />
                  <AvatarFallback className="text-4xl">
                    <img src={DEFAULT_AVATAR} alt="Default" className="w-full h-full object-cover" />
                  </AvatarFallback>
                </Avatar>
                {isOwnProfile && (
                  <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                    <Camera className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                )}
              </div>

              <div className="mt-4 text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <h1 className="font-serif text-2xl md:text-3xl font-bold" data-testid="profile-display-name">
                    {profile.display_name || profile.username}
                  </h1>
                  {getRoleBadge(profile.role)}
                  {profile.premium_tier > 0 && (
                    <TierBadge tier={profile.premium_tier} roleName={profile.premium_role_name} size="md" />
                  )}
                  {/* Custom badges assigned by admin */}
                  {(profile.badges || []).map(bId => {
                    const b = allBadges.find(x => x.badge_id === bId);
                    return b ? (
                      <span
                        key={bId}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
                        style={{ backgroundColor: b.bg_color, color: b.text_color }}
                        data-testid={`profile-badge-${bId}`}
                      >
                        {b.text}
                      </span>
                    ) : null;
                  })}
                </div>

                {/* Headline */}
                {profile.profile_headline && (
                  <p className="text-sm text-muted-foreground font-medium mb-1" data-testid="profile-headline">
                    {profile.profile_headline}
                  </p>
                )}

                {/* Real Name (only if available and viewer has permission) */}
                {profile.real_name && (
                  <p className="text-sm text-muted-foreground mb-1" data-testid="profile-real-name">
                    {profile.real_name}
                  </p>
                )}

                {/* Account Name (admin only) */}
                {isAdmin && profile.account_name && (
                  <p className="text-xs text-muted-foreground/60 mb-1" data-testid="profile-account-name">
                    <Shield className="w-3 h-3 inline mr-1" />Hidden Name: {profile.account_name}
                  </p>
                )}

                {/* Email (admin only) */}
                {isAdmin && profile.email && (
                  <p className="text-xs text-muted-foreground/60 mb-1" data-testid="profile-email-admin">
                    <Shield className="w-3 h-3 inline mr-1" />Email: {profile.email}
                  </p>
                )}

                <p className="text-muted-foreground text-sm flex items-center justify-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Joined {joinDate}
                </p>

                {/* Confidants Count */}
                {profile.confidants_count !== undefined && (
                  <p className="text-muted-foreground text-sm flex items-center justify-center gap-1 mt-1" data-testid="profile-confidants-count">
                    <Users className="w-4 h-4" />
                    {profile.confidants_count} Confidant{profile.confidants_count !== 1 ? 's' : ''}
                  </p>
                )}

                {/* Confidant Relationship Status */}
                {!isOwnProfile && currentUser && (
                  <div className="mt-3" data-testid="confidant-status-section">
                    {confidantStatus === 'confidants' && (
                      <Badge className="bg-forest-600 text-white" data-testid="confidant-badge">
                        <Users className="w-3 h-3 mr-1" /> Confidant
                      </Badge>
                    )}
                    {confidantStatus === 'pending_sent' && (
                      <Badge variant="outline" className="text-amber-600 border-amber-300 dark:border-amber-700" data-testid="pending-sent-badge">
                        <Clock className="w-3 h-3 mr-1" /> Request Sent
                      </Badge>
                    )}
                    {confidantStatus === 'pending_received' && (
                      <Badge variant="outline" className="text-blue-600 border-blue-300 dark:border-blue-700" data-testid="pending-received-badge">
                        <UserPlus className="w-3 h-3 mr-1" /> Wants to be your confidant
                      </Badge>
                    )}
                    {confidantStatus === 'cooldown' && (
                      <Badge variant="outline" className="text-muted-foreground" data-testid="cooldown-badge">
                        <Clock className="w-3 h-3 mr-1" /> Request cooldown active
                      </Badge>
                    )}
                    {confidantStatus === 'none' && !blockStatus && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-forest-600 border-forest-300 dark:border-forest-700 hover:bg-forest-50 dark:hover:bg-forest-900/20"
                        onClick={() => setShowConfidantDialog(true)}
                        data-testid="send-confidant-request-btn"
                      >
                        <UserPlus className="w-4 h-4 mr-1" />
                        Request Confidant
                      </Button>
                    )}
                  </div>
                )}

                {/* Profile Completeness */}
                {isOwnProfile && completion && completion.pct < 100 && (
                  <div className="mt-3" data-testid="profile-completion-bar">
                    <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                      Profile {completion.pct}% complete ({completion.filled}/{completion.total})
                    </p>
                    <div className="w-40 mx-auto h-2 bg-muted rounded-full mt-1 overflow-hidden">
                      <div
                        className="h-full bg-amber-500 rounded-full transition-all duration-500"
                        style={{ width: `${completion.pct}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Private Photo Section */}
            {(isOwnProfile || profile.profile_image_url_private) && (
              <div className="mt-6 flex flex-col items-center" data-testid="private-photo-section">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground">Private Photo</span>
                </div>
                <div
                  className={`relative ${isOwnProfile || profile.profile_image_url_private ? 'cursor-pointer group' : ''}`}
                  onClick={handlePrivatePhotoClick}
                  data-testid="private-photo-container"
                >
                  <Avatar className="w-20 h-20 border-2 border-dashed border-muted-foreground/30">
                    {profile.profile_image_url_private ? (
                      <ProfileAvatarImage
                        src={getProfileImageUrl(profile.profile_image_url_private)}
                        alt="Private"
                        zoom={profile.profile_image_private_zoom}
                        positionX={profile.profile_image_private_position_x}
                        positionY={profile.profile_image_private_position_y}
                      />
                    ) : null}
                    <AvatarFallback className="text-sm bg-muted/50">
                      {isOwnProfile ? <ImagePlus className="w-6 h-6 text-muted-foreground/50" /> : <Lock className="w-6 h-6 text-muted-foreground/50" />}
                    </AvatarFallback>
                  </Avatar>
                  {isOwnProfile && (
                    <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                      <Camera className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  )}
                </div>
                {isOwnProfile && !profile.profile_image_url_private && (
                  <p className="text-xs text-muted-foreground mt-1">Click to add a private photo</p>
                )}
                <p className="text-[10px] text-muted-foreground/60 mt-1">Visible only to confidants and certain roles</p>
              </div>
            )}

            {/* Bio */}
            {profile.bio && (
              <div className="mt-6 text-center max-w-md mx-auto">
                <p className="text-foreground/80 italic leading-relaxed" data-testid="profile-bio">
                  "{profile.bio}"
                </p>
                {!isOwnProfile && (
                  <p className="text-[10px] text-muted-foreground/60 mt-1">Visible only to confidants and certain roles</p>
                )}
              </div>
            )}

            {/* Tags */}
            {(profile.tags?.length > 0 || isAdmin) && (
              <div className="mt-6" data-testid="profile-tags">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Tag className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">Tags</span>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  {profile.tags?.map((tag, i) => (
                    <Badge
                      key={`${tag.tag_name}-${i}`}
                      variant={tag.type === 'auto' ? 'secondary' : 'outline'}
                      className={`${tag.type === 'auto' ? 'border-amber-500/50 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400' : 'border-forest-300 dark:border-forest-700'}`}
                      data-testid={`profile-tag-${tag.tag_name}`}
                    >
                      {tag.tag_name}
                      {isAdmin && tag.type === 'manual' && (
                        <button
                          onClick={() => handleRemoveTag(tag.tag_name)}
                          className="ml-1 hover:text-destructive"
                          data-testid={`remove-tag-${tag.tag_name}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </Badge>
                  ))}
                  {profile.tags?.length === 0 && !isAdmin && (
                    <span className="text-xs text-muted-foreground italic">No tags yet</span>
                  )}
                </div>
                {isAdmin && (
                  <div className="flex items-center justify-center gap-2 mt-3">
                    <Input
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder="Add a tag..."
                      className="max-w-[200px] h-8 text-sm"
                      onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                      data-testid="add-tag-input"
                    />
                    <Button size="sm" variant="outline" onClick={handleAddTag} disabled={addingTag || !newTag.trim()} data-testid="add-tag-btn">
                      {addingTag ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Edit Button */}
            {isOwnProfile && (
              <div className="mt-6 text-center">
                <Button
                  variant="outline"
                  onClick={openEditProfile}
                  data-testid="edit-profile-btn"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit Profile
                </Button>
              </div>
            )}

            {/* Block User Button (for logged-in non-admin users viewing another profile) */}
            {currentUser && !isOwnProfile && !isAdmin && (
              <div className="mt-4 text-center">
                <div className="flex flex-wrap items-center justify-center gap-2">
                  {canReport && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                      onClick={() => {
                        setReportNameReason('');
                        setShowReportNameDialog(true);
                      }}
                      data-testid="report-username-btn"
                    >
                      <Flag className="w-4 h-4 mr-2" />
                      Report Username
                    </Button>
                  )}
                  {blockStatus ? (
                    <Badge variant="outline" className="text-red-600 border-red-300 dark:border-red-800" data-testid="blocked-badge">
                      <Ban className="w-3 h-3 mr-1" /> You have blocked this user
                    </Badge>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                      onClick={() => setShowBlockConfirm(true)}
                      data-testid="block-user-btn"
                    >
                      <Ban className="w-4 h-4 mr-2" />
                      Block User
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Admin Moderation Panel */}
            {isAdmin && !isOwnProfile && modInfo && (
              <div className="mt-6 border-t pt-6" data-testid="admin-moderation-panel">
                <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4" /> Admin Moderation
                </h3>
                <div className="flex flex-wrap gap-2 mb-4 justify-center">
                  {modInfo.is_banned && (
                    <Badge className="bg-red-600 text-white" data-testid="banned-status">
                      <Ban className="w-3 h-3 mr-1" /> Banned
                      {modInfo.ban_reason && ` - ${modInfo.ban_reason}`}
                    </Badge>
                  )}
                  {modInfo.is_muted && (
                    <Badge className="bg-amber-600 text-white" data-testid="muted-status">
                      <VolumeX className="w-3 h-3 mr-1" /> Muted until {format(new Date(modInfo.muted_until), 'MMM d, yyyy')}
                      {modInfo.mute_reason && ` - ${modInfo.mute_reason}`}
                    </Badge>
                  )}
                  <Badge variant="outline" data-testid="blocks-by-count">
                    Blocks made: {modInfo.blocks_by_user}
                  </Badge>
                  <Badge variant="outline" data-testid="blocks-against-count">
                    Blocked by: {modInfo.blocks_against_user}
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-2 justify-center">
                  <input
                    ref={adminBannerInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAdminBannerSelect}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => adminBannerInputRef.current?.click()}
                    disabled={updatingBanner}
                    data-testid="admin-set-cover-btn"
                  >
                    {updatingBanner ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-1" />}
                    Set Cover
                  </Button>
                  {!modInfo.is_muted && !modInfo.is_banned && (
                    <Button
                      size="sm" variant="outline"
                      className="text-amber-600 border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                      onClick={() => setShowMuteDialog(true)}
                      data-testid="mute-user-btn"
                    >
                      <VolumeX className="w-4 h-4 mr-1" /> Mute
                    </Button>
                  )}
                  {modInfo.is_muted && (
                    <Button
                      size="sm" variant="outline"
                      className="text-green-600 border-green-300 hover:bg-green-50 dark:hover:bg-green-900/20"
                      onClick={handleUnmuteUser}
                      data-testid="unmute-user-btn"
                    >
                      Unmute
                    </Button>
                  )}
                  {!modInfo.is_banned ? (
                    <Button
                      size="sm" variant="outline"
                      className="text-red-600 border-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
                      onClick={() => setShowBanDialog(true)}
                      data-testid="ban-user-btn"
                    >
                      <Ban className="w-4 h-4 mr-1" /> Ban
                    </Button>
                  ) : (
                    <Button
                      size="sm" variant="outline"
                      className="text-green-600 border-green-300 hover:bg-green-50 dark:hover:bg-green-900/20"
                      onClick={handleUnbanUser}
                      data-testid="unban-user-btn"
                    >
                      <ShieldOff className="w-4 h-4 mr-1" /> Unban
                    </Button>
                  )}
                  {modInfo.blocks_by_user > 0 && (
                    <Button
                      size="sm" variant="outline"
                      onClick={handleClearBlocks}
                      data-testid="clear-blocks-btn"
                    >
                      <Eraser className="w-4 h-4 mr-1" /> Clear Blocklist ({modInfo.blocks_by_user})
                    </Button>
                  )}
                  {blockStatus ? (
                    <Badge variant="outline" className="text-red-600 border-red-300">
                      <Ban className="w-3 h-3 mr-1" /> You blocked this user
                    </Badge>
                  ) : (
                    <Button
                      variant="ghost" size="sm"
                      className="text-red-500 hover:text-red-600"
                      onClick={() => setShowBlockConfirm(true)}
                      data-testid="admin-block-user-btn"
                    >
                      <Ban className="w-4 h-4 mr-1" /> Block
                    </Button>
                  )}
                </div>
                {/* Badge Assignment */}
                {allBadges.length > 0 && (
                  <div className="mt-3 border-t pt-3">
                    <h4 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                      <Crown className="w-3 h-3" /> Badges
                    </h4>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {allBadges.map(badge => {
                        const has = (profile.badges || []).includes(badge.badge_id);
                        return (
                          <button
                            key={badge.badge_id}
                            onClick={async () => {
                              try {
                                if (has) {
                                  await axios.delete(`${API_URL}/api/admin/users/${profile.user_id}/badges/${badge.badge_id}`);
                                } else {
                                  await axios.post(`${API_URL}/api/admin/users/${profile.user_id}/badges/${badge.badge_id}`);
                                }
                                setProfile(p => ({
                                  ...p,
                                  badges: has
                                    ? (p.badges || []).filter(id => id !== badge.badge_id)
                                    : [...(p.badges || []), badge.badge_id]
                                }));
                                toast.success(has ? 'Badge removed' : 'Badge assigned');
                              } catch { toast.error('Failed to update badge'); }
                            }}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold transition-all ${has ? 'ring-2 ring-offset-1 ring-primary opacity-100' : 'opacity-50 hover:opacity-80'}`}
                            style={{ backgroundColor: badge.bg_color, color: badge.text_color }}
                            data-testid={`profile-toggle-badge-${badge.badge_id}`}
                          >
                            {badge.text}
                            {has && <span className="ml-0.5">✓</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 gap-4" data-testid="profile-stats">
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-forest-700 dark:text-forest-400" data-testid="stat-comments">
                {stats?.comment_count ?? '—'}
              </p>
              <p className="text-sm text-muted-foreground">Comments</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-forest-700 dark:text-forest-400" data-testid="stat-reactions-given">
                {stats?.reactions_given ?? '—'}
              </p>
              <p className="text-sm text-muted-foreground">Reactions Given</p>
            </CardContent>
          </Card>
        </div>

        {/* Reactions Received Breakdown */}
        {stats && stats.total_reactions_received > 0 && (
          <Card className="mt-4" data-testid="reactions-received-card">
            <CardContent className="pt-6">
              <h3 className="text-sm font-medium text-muted-foreground mb-3 text-center">Reactions Received</h3>
              <div className="flex flex-wrap items-center justify-center gap-3">
                {Object.entries(stats.reactions_received)
                  .sort(([ a], [ b]) => b - a)
                  .map(([emoji, count]) => (
                    <div key={emoji} className="flex items-center gap-1 bg-muted/50 rounded-full px-3 py-1.5">
                      <span className="text-lg">{emoji}</span>
                      <span className="text-sm font-semibold">{count}</span>
                    </div>
                  ))
                }
              </div>
              <p className="text-xs text-muted-foreground text-center mt-2">
                {stats.total_reactions_received} total reactions received
              </p>
            </CardContent>
          </Card>
        )}

        {/* Photo Options Dialog (View / Change) - own profile */}
        <Dialog open={showPhotoOptionsDialog} onOpenChange={setShowPhotoOptionsDialog}>
          <DialogContent className="sm:max-w-xs">
            <DialogHeader>
              <DialogTitle>{photoOptionsType === 'public' ? 'Profile Photo' : 'Private Photo'}</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-2 py-2">
              {((photoOptionsType === 'public' && profile?.profile_picture) || (photoOptionsType === 'private' && profile?.profile_image_url_private)) && (
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => viewCurrentPhoto(photoOptionsType)}
                  data-testid="view-photo-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Photo
                </Button>
              )}
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => openUploadDialog(photoOptionsType)}
                data-testid="change-photo-btn"
              >
                <Camera className="w-4 h-4 mr-2" />
                {((photoOptionsType === 'public' && profile?.profile_picture) || (photoOptionsType === 'private' && profile?.profile_image_url_private)) ? 'Change Photo' : 'Upload Photo'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Photo View Dialog */}
        <Dialog open={showPhotoViewDialog} onOpenChange={setShowPhotoViewDialog}>
          <DialogContent className="sm:max-w-lg p-2">
            {viewingPhotoUrl && (
              <img
                src={viewingPhotoUrl}
                alt="Profile"
                className="w-full h-auto rounded-lg max-h-[70vh] object-contain"
                data-testid="photo-view-image"
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Profile Image Upload Dialog */}
        <Dialog open={showUploadDialog} onOpenChange={(open) => { setShowUploadDialog(open); if (!open) { setPreviewUrl(null); } }}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>
                {uploadType === 'private' ? 'Update Private Photo' : 'Update Profile Photo'}
              </DialogTitle>
              <DialogDescription>
                Upload a new image (JPEG, PNG, WebP, or GIF, max 5MB)
                {uploadType === 'private' && (
                  <span className="block mt-1 text-amber-600 dark:text-amber-400">Visible only to confidants and certain roles</span>
                )}
                {uploadType === 'public' && (
                  <span className="block mt-1">Visible to all logged in users</span>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="flex justify-center">
                <div className="space-y-2">
                  <div
                    ref={previewGestureRef}
                    className="rounded-full"
                    style={{ touchAction: 'none' }}
                    onPointerDown={handlePreviewPointerDown}
                    onPointerMove={handlePreviewPointerMove}
                    onPointerUp={handlePreviewPointerUp}
                    onPointerCancel={handlePreviewPointerUp}
                    onWheel={handlePreviewWheel}
                    onTouchStart={handlePreviewTouchStart}
                    onTouchMove={handlePreviewTouchMove}
                    onTouchEnd={handlePreviewTouchEnd}
                    data-testid="photo-preview-gesture-surface"
                  >
                    <Avatar className="w-32 h-32 border-2 border-border select-none">
                      <ProfileAvatarImage
                        src={previewUrl || getProfileImageUrl(uploadType === 'private' ? profile?.profile_image_url_private : profile?.profile_picture)}
                        alt="Preview"
                        zoom={imageFrame.zoom}
                        positionX={imageFrame.positionX}
                        positionY={imageFrame.positionY}
                        draggable={false}
                      />
                      <AvatarFallback>
                        <img src={DEFAULT_AVATAR} alt="Default" className="w-full h-full object-cover" />
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  <p className="text-center text-xs text-muted-foreground">
                    Drag to reposition. Pinch on touchscreens or use the mouse wheel to zoom.
                  </p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Zoom</span>
                    <span>{imageFrame.zoom.toFixed(2)}x</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="3"
                    step="0.01"
                    value={imageFrame.zoom}
                    onChange={(e) => setImageFrame((frame) => ({ ...frame, zoom: Number(e.target.value) }))}
                    className="w-full"
                    data-testid="photo-zoom-slider"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Horizontal Position</span>
                    <span>{Math.round(imageFrame.positionX)}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={imageFrame.positionX}
                    onChange={(e) => setImageFrame((frame) => ({ ...frame, positionX: Number(e.target.value) }))}
                    className="w-full"
                    data-testid="photo-position-x-slider"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Vertical Position</span>
                    <span>{Math.round(imageFrame.positionY)}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={imageFrame.positionY}
                    onChange={(e) => setImageFrame((frame) => ({ ...frame, positionY: Number(e.target.value) }))}
                    className="w-full"
                    data-testid="photo-position-y-slider"
                  />
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="profile-image-file-input"
              />
              <Button
                variant="outline"
                className="w-full"
                onClick={() => fileInputRef.current?.click()}
                data-testid="choose-image-btn"
              >
                <Camera className="w-4 h-4 mr-2" />
                Choose Image
              </Button>
              {previewUrl && (
                <Button
                  className="w-full"
                  onClick={handleUpload}
                  disabled={uploading}
                  data-testid="upload-image-btn"
                >
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {uploading ? 'Uploading...' : 'Save Photo'}
                </Button>
              )}
              {!previewUrl && ((uploadType === 'private' ? profile?.profile_image_url_private : profile?.profile_picture)) && (
                <Button
                  className="w-full"
                  onClick={handleUpload}
                  disabled={uploading}
                  data-testid="save-photo-framing-btn"
                >
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {uploading ? 'Saving...' : 'Save Framing'}
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Block Confirmation Dialog */}
        <Dialog open={showReportNameDialog} onOpenChange={setShowReportNameDialog}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Report Username</DialogTitle>
              <DialogDescription>
                This username will be reviewed by a moderator.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <label className="text-sm font-medium mb-1 block">Reason (optional)</label>
              <textarea
                value={reportNameReason}
                onChange={(e) => setReportNameReason(e.target.value)}
                placeholder="Why are you reporting this username?"
                rows={3}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="report-username-reason-input"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowReportNameDialog(false)}>Cancel</Button>
              <Button
                className="bg-amber-600 hover:bg-amber-700 text-white"
                onClick={handleReportDisplayName}
                disabled={reportingName}
                data-testid="confirm-report-username-btn"
              >
                {reportingName ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Flag className="w-4 h-4 mr-1" />}
                Submit Report
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Block Confirmation Dialog */}
        <Dialog open={showBlockConfirm} onOpenChange={setShowBlockConfirm}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Block {profile?.display_name || profile?.username}?</DialogTitle>
              <DialogDescription>
                This action is permanent and cannot be undone by you. You will no longer see their comments, and they won't see yours. Only an admin can undo this.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowBlockConfirm(false)}>Cancel</Button>
              <Button
                variant="destructive"
                onClick={handleBlockUser}
                disabled={blocking}
                data-testid="confirm-block-btn"
              >
                {blocking ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Ban className="w-4 h-4 mr-1" />}
                Block User
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Mute Dialog */}
        <Dialog open={showMuteDialog} onOpenChange={setShowMuteDialog}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Mute {profile?.display_name || profile?.username}</DialogTitle>
              <DialogDescription>
                Muted users cannot post comments or reactions for the specified duration.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Duration</label>
                <Select value={muteDays} onValueChange={setMuteDays}>
                  <SelectTrigger data-testid="mute-duration-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 day</SelectItem>
                    <SelectItem value="3">3 days</SelectItem>
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="14">14 days</SelectItem>
                    <SelectItem value="28">28 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Reason (optional)</label>
                <Input
                  value={muteReason}
                  onChange={(e) => setMuteReason(e.target.value)}
                  placeholder="Why is this user being muted?"
                  data-testid="mute-reason-input"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowMuteDialog(false)}>Cancel</Button>
              <Button
                className="bg-amber-600 hover:bg-amber-700 text-white"
                onClick={handleMuteUser}
                data-testid="confirm-mute-btn"
              >
                <VolumeX className="w-4 h-4 mr-1" /> Mute User
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Ban Dialog */}
        <Dialog open={showBanDialog} onOpenChange={setShowBanDialog}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Ban {profile?.display_name || profile?.username}?</DialogTitle>
              <DialogDescription>
                This will permanently prevent the user from commenting or reacting. This can be reversed by an admin.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Reason (optional)</label>
                <Input
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  placeholder="Why is this user being banned?"
                  data-testid="ban-reason-input"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowBanDialog(false)}>Cancel</Button>
              <Button
                variant="destructive"
                onClick={handleBanUser}
                data-testid="confirm-ban-btn"
              >
                <Ban className="w-4 h-4 mr-1" /> Ban Permanently
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Profile Dialog */}
        <Dialog open={showEditProfile} onOpenChange={setShowEditProfile}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Profile</DialogTitle>
              <DialogDescription>Update your profile information.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              {/* Public Section */}
              <div className="border-b pb-3">
                <p className="text-xs font-semibold text-forest-600 dark:text-forest-400 uppercase tracking-wide mb-3">
                  Visible to all logged in users
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Display Name</label>
                    <Input
                      value={editDisplayName}
                      onChange={(e) => setEditDisplayName(e.target.value)}
                      placeholder="Your display name (letters, numbers, spaces)"
                      data-testid="edit-display-name"
                    />
                    <p className="text-xs text-muted-foreground mt-1">This is how other members see you. Must be unique.</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Headline</label>
                    <Input
                      value={editHeadline}
                      onChange={(e) => setEditHeadline(e.target.value.slice(0, 100))}
                      placeholder="A short headline about yourself"
                      maxLength={100}
                      data-testid="edit-headline"
                    />
                    <p className="text-xs text-muted-foreground mt-1">{editHeadline.length}/100 characters</p>
                  </div>
                </div>
              </div>

              {/* Private Section */}
              <div>
                <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide mb-3">
                  Visible only to confidants and certain roles
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Real Name (optional)</label>
                    <Input
                      value={editRealName}
                      onChange={(e) => setEditRealName(e.target.value)}
                      placeholder="First and last name (letters only)"
                      data-testid="edit-real-name"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Bio / About Me</label>
                    <textarea
                      value={editBio}
                      onChange={(e) => setEditBio(e.target.value)}
                      placeholder="Tell the community about yourself..."
                      rows={3}
                      className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      data-testid="edit-bio"
                    />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowEditProfile(false)}>Cancel</Button>
              <Button onClick={handleSaveProfile} disabled={savingProfile} data-testid="save-profile-btn">
                {savingProfile ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confidant Request Dialog */}
        <Dialog open={showConfidantDialog} onOpenChange={setShowConfidantDialog}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Send Confidant Request</DialogTitle>
              <DialogDescription>
                Becoming confidants lets you see each other's private photos, bio, and real name.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <label className="text-sm font-medium mb-1 block">Message (optional)</label>
                <textarea
                  value={confidantMessage}
                  onChange={(e) => setConfidantMessage(e.target.value.slice(0, 300))}
                  placeholder="Why would you like to connect?"
                  rows={3}
                  maxLength={300}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="confidant-message-input"
                />
                <p className="text-xs text-muted-foreground mt-1">{confidantMessage.length}/300 characters</p>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowConfidantDialog(false)}>Cancel</Button>
              <Button
                onClick={handleSendConfidantRequest}
                disabled={sendingRequest}
                data-testid="send-confidant-request-confirm-btn"
              >
                {sendingRequest ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                Send Request
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </motion.div>
    </div>
  );
}
